// Class to handle Trustpilot product reviews summary display
class TrustpilotProductRating {
  constructor(config) {
    this.businessUnitId = config.businessUnitId;
    this.apiKey = config.apiKey;
    this.skus = config.skus;
    this.container = document.querySelector('.js-trustpilot-product-rating');
    this.starIconUrl = config.starIconUrl;
    this.minRating = config.minRating;
    this.translations = (window.translations && window.translations.trustpilot) || {};
  }

  // Fetch product reviews summary from Trustpilot API (aggregates all variant SKUs)
  async fetchProductReviewsSummary() {
    try {
      const response = await fetch(
        `https://api.trustpilot.com/v1/product-reviews/business-units/${this.businessUnitId}?sku=${encodeURIComponent(this.skus)}`,
        {
          headers: {
            ApiKey: this.apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console -- Trustpilot error fetching product reviews
      console.error('Error fetching product reviews summary:', error);
      return null;
    }
  }

  // Create and return star rating element
  renderStarIcon() {
    const img = document.createElement('img');
    img.src = this.starIconUrl;
    img.alt = this.translations.starAlt || 'Star';
    img.width = 20;
    img.height = 21;
    img.className = 'trustpilot-product-rating__star-icon';
    return img;
  }

  // Render the product rating summary
  renderRatingSummary(data) {
    if (!this.container) return;

    // Find the existing content element (preserves the <a> tag wrapper if present)
    const ratingContent = this.container.querySelector('.trustpilot-product-rating__content');
    if (!ratingContent) return;

    const starsAverage = data.starsAverage;
    const totalReviews = data.numberOfReviews?.total;

    if (!starsAverage || !totalReviews) {
      this.container.style.display = 'none';
      return;
    }

    if (starsAverage < this.minRating) {
      this.container.style.display = 'none';
      return;
    }

    // Create star icon
    const starIcon = this.renderStarIcon();

    // Create average rating text
    const averageSpan = document.createElement('span');
    averageSpan.className = 'trustpilot-product-rating__average';
    averageSpan.textContent = starsAverage.toFixed(1);

    // Create average rating supporting text
    const basedOnSpan = document.createElement('span');
    basedOnSpan.className = 'trustpilot-product-rating__based-on';
    basedOnSpan.textContent = ` ${this.translations.basedOn} ` || ` based on `;

    // Create review count text
    const countSpan = document.createElement('span');
    countSpan.className = 'trustpilot-product-rating__count';
    countSpan.textContent = `${totalReviews.toLocaleString()}`;

    // Create review count supporting text
    const reviewsSpan = document.createElement('span');
    reviewsSpan.className = 'trustpilot-product-rating__reviews';
    reviewsSpan.textContent =
      totalReviews === 1
        ? ` ${this.translations.review} ` || ` review `
        : ` ${this.translations.reviews} ` || ` reviews `;

    // Populate the existing content element
    ratingContent.innerHTML = '';
    ratingContent.appendChild(starIcon);
    ratingContent.appendChild(averageSpan);
    ratingContent.appendChild(basedOnSpan);
    ratingContent.appendChild(countSpan);
    ratingContent.appendChild(reviewsSpan);

    this.container.classList.add('is-loaded');
  }

  // Initialise product rating display
  async init() {
    if (!this.skus || !this.container) {
      return;
    }

    const data = await this.fetchProductReviewsSummary();
    if (data) {
      this.renderRatingSummary(data);
    }
  }
}

// Start product rating component when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const config = window.trustpilotProductConfig || {};
  if (config.skus) {
    const trustpilotProductRating = new TrustpilotProductRating(config);
    trustpilotProductRating.init();
  }
});
